/**
 * ArtifactItemFilterEditor Component
 * 
 * UI for defining filters to find specific artifact items.
 * Supports filtering by property values and states.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Space, Select, Input, InputNumber, Button, Card } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ArtifactItemFilter, PropertyFilter } from '../../types/transitionParameters';
import { PropertySelector } from './PropertySelector';
import { apiClient } from '../../services/apiClient';

const { Option } = Select;

interface ArtifactItemFilterEditorProps {
  filter?: ArtifactItemFilter;
  onChange: (filter: ArtifactItemFilter | undefined) => void;
  primeArtifacts?: Array<{ id: string; name: string }>; // Preferred: select specific prime artifact
  artifactTypes?: Array<{ id: string; name: string }>; // Fallback: filter by artifact type
  disabled?: boolean;
}

interface PropertyDefinition {
  property_key?: string;
  name?: string;
  property_type?: string;
  type?: string;
}

export const ArtifactItemFilterEditor: React.FC<ArtifactItemFilterEditorProps> = ({
  filter,
  onChange,
  primeArtifacts = [],
  artifactTypes = [],
  disabled = false,
}) => {
  const [sourceArtifactProperties, setSourceArtifactProperties] = useState<PropertyDefinition[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Fetch properties for the selected prime artifact (preferred) or artifact type (fallback)
  const fetchProperties = useCallback(async (artifactId?: string, artifactTypeName?: string) => {
    if (!artifactId && !artifactTypeName) {
      setSourceArtifactProperties([]);
      return;
    }
    setLoadingProperties(true);
    try {
      if (artifactId && primeArtifacts.length > 0) {
        // Fetch properties from prime artifact
        const response = await apiClient.get(`/prime-artifacts/${artifactId}/properties`);
        const artifactData = response.data;
        const properties = Array.isArray(artifactData) ? artifactData : (artifactData?.properties || artifactData?.items || []);
        setSourceArtifactProperties(Array.isArray(properties) ? properties : []);
      } else if (artifactTypeName && artifactTypes && artifactTypes.length > 0) {
        // Fallback: fetch properties from artifact type
        const artifactType = artifactTypes.find(t => t.name === artifactTypeName);
        if (artifactType) {
          const response = await apiClient.get(`/artifact-types/${artifactType.id}/properties`);
          const typeData = response.data;
          let properties = typeData?.properties;
          if (!Array.isArray(properties)) {
            if (properties && typeof properties === 'object') {
              properties = Object.values(properties);
            } else {
              properties = [];
            }
          }
          setSourceArtifactProperties(Array.isArray(properties) ? properties : []);
        } else {
          setSourceArtifactProperties([]);
        }
      } else {
        setSourceArtifactProperties([]);
      }
    } catch (error) {
      console.error('Error fetching artifact properties:', error);
      setSourceArtifactProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  }, [primeArtifacts, artifactTypes]);

  // Fetch properties when artifact ID or type changes
  // Use refs to track previous values to avoid infinite loops
  const prevArtifactIdRef = useRef<string | undefined>(undefined);
  const prevArtifactTypeRef = useRef<string | undefined>(undefined);
  const isFetchingRef = useRef(false);
  
  // Memoize the current values to avoid unnecessary re-renders
  const currentArtifactId = filter?.artifact_id;
  const currentArtifactType = filter?.artifact_type;
  
  useEffect(() => {
    // Skip if already fetching to prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    
    // Only fetch if values actually changed (using strict equality)
    const artifactIdChanged = currentArtifactId !== prevArtifactIdRef.current;
    const artifactTypeChanged = currentArtifactType !== prevArtifactTypeRef.current;
    
    if (!artifactIdChanged && !artifactTypeChanged) {
      return; // No change, skip
    }
    
    // Update refs immediately to prevent re-triggering
    prevArtifactIdRef.current = currentArtifactId;
    prevArtifactTypeRef.current = currentArtifactType;
    
    // Set fetching flag
    isFetchingRef.current = true;
    
    // Fetch properties
    const fetchPromise = currentArtifactId
      ? fetchProperties(currentArtifactId)
      : currentArtifactType
      ? fetchProperties(undefined, currentArtifactType)
      : Promise.resolve();
    
    fetchPromise
      .then(() => {
        isFetchingRef.current = false;
      })
      .catch((err) => {
        console.error('Error in fetchProperties:', err);
        isFetchingRef.current = false;
        setSourceArtifactProperties([]);
      });
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArtifactId, currentArtifactType]); // Only depend on the actual values, not the filter object

  const handleFilterUpdate = (field: keyof ArtifactItemFilter, value: any) => {
    // Only update if value actually changed to prevent unnecessary re-renders
    const currentValue = filter?.[field];
    if (currentValue === value) {
      return; // No change, skip update
    }
    
    const updated: ArtifactItemFilter = {
      ...filter,
      [field]: value,
    };
    onChange(updated);
  };

  const handleAddPropertyFilter = () => {
    const newFilter: PropertyFilter = {
      property_key: '',
      operator: 'equals',
      value: '',
    };
    const updated: ArtifactItemFilter = {
      ...filter,
      property_filters: [...(filter?.property_filters || []), newFilter],
    };
    onChange(updated);
  };

  const handleRemovePropertyFilter = (index: number) => {
    const updated: ArtifactItemFilter = {
      ...filter,
      property_filters: filter?.property_filters?.filter((_, i) => i !== index),
    };
    onChange(updated);
  };

  const handlePropertyFilterUpdate = (index: number, field: keyof PropertyFilter, value: any) => {
    const updated: ArtifactItemFilter = {
      ...filter,
      property_filters: filter?.property_filters?.map((pf, i) =>
        i === index ? { ...pf, [field]: value } : pf
      ),
    };
    onChange(updated);
  };

  const renderPropertyValueInput = (propertyFilter: PropertyFilter, index: number) => {
    // Ensure sourceArtifactProperties is always an array before calling .find()
    const propertiesArray = Array.isArray(sourceArtifactProperties) ? sourceArtifactProperties : [];
    const property = propertiesArray.find(
      (p: PropertyDefinition) => (p.property_key || p.name) === propertyFilter.property_key
    );
    const propertyType = property?.property_type || property?.type || 'string';

    if (propertyType === 'number' || propertyType === 'integer' || propertyType === 'float') {
      return (
        <InputNumber
          value={propertyFilter.value}
          onChange={(value) => handlePropertyFilterUpdate(index, 'value', value)}
          disabled={disabled}
          style={{ width: '100%' }}
          placeholder="Enter value"
        />
      );
    } else if (propertyType === 'boolean') {
      return (
        <Select
          value={propertyFilter.value}
          onChange={(value) => handlePropertyFilterUpdate(index, 'value', value)}
          disabled={disabled}
          style={{ width: '100%' }}
        >
          <Option value={true}>True</Option>
          <Option value={false}>False</Option>
        </Select>
      );
    } else {
      return (
        <Input
          value={propertyFilter.value}
          onChange={(e) => handlePropertyFilterUpdate(index, 'value', e.target.value)}
          disabled={disabled}
          placeholder="Enter value"
        />
      );
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <div>
        <label style={{ display: 'block', marginBottom: 4 }}>Artifact Type:</label>
        <Select
          value={filter?.artifact_type}
          onChange={(value) => handleFilterUpdate('artifact_type', value || undefined)}
          disabled={disabled}
          style={{ width: '100%' }}
          placeholder="Select artifact type"
          allowClear
          showSearch
          filterOption={(input, option) => {
            const label = option?.label || option?.children;
            return String(label).toLowerCase().includes(input.toLowerCase());
          }}
        >
          {artifactTypes.map(type => (
            <Option key={type.id} value={type.name}>
              {type.name}
            </Option>
          ))}
        </Select>
      </div>

      {(filter?.artifact_id || filter?.artifact_type) && (
        <>
          <div>
            <label style={{ display: 'block', marginBottom: 4 }}>Property Filters:</label>
            {filter?.property_filters && filter.property_filters.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {filter?.property_filters.map((propertyFilter, index) => (
                  <Card key={index} size="small" style={{ backgroundColor: '#fafafa' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <Space style={{ width: '100%' }}>
                        <PropertySelector
                          properties={Array.isArray(sourceArtifactProperties) ? sourceArtifactProperties : []}
                          value={propertyFilter.property_key}
                          onChange={(key) => handlePropertyFilterUpdate(index, 'property_key', key)}
                          disabled={disabled || loadingProperties}
                          placeholder="Select property"
                          style={{ flex: 1 }}
                        />
                        <Select
                          value={propertyFilter.operator}
                          onChange={(value) => handlePropertyFilterUpdate(index, 'operator', value)}
                          disabled={disabled}
                          style={{ width: 150 }}
                        >
                          <Option value="equals">Equals</Option>
                          <Option value="not_equals">Not Equals</Option>
                          <Option value="greater_than">Greater Than</Option>
                          <Option value="less_than">Less Than</Option>
                          <Option value="greater_or_equal">Greater or Equal</Option>
                          <Option value="less_or_equal">Less or Equal</Option>
                          <Option value="contains">Contains</Option>
                          <Option value="in">In (comma-separated)</Option>
                        </Select>
                        {!disabled && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemovePropertyFilter(index)}
                          />
                        )}
                      </Space>
                      <div style={{ width: '100%' }}>
                        {renderPropertyValueInput(propertyFilter, index)}
                      </div>
                    </Space>
                  </Card>
                ))}
              </Space>
            ) : (
              <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic', marginBottom: 8 }}>
                No property filters. All items of this type will be included.
              </div>
            )}
            {!disabled && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddPropertyFilter}
                style={{ width: '100%', marginTop: 8 }}
              >
                Add Property Filter
              </Button>
            )}
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Property filters use AND logic (all conditions must match)
            </div>
          </div>
        </>
      )}
    </Space>
  );
};
